import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, Renderer2, ChangeDetectorRef, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig } from '@angular/material/legacy-dialog';
import { ModalService } from '@core/services/modal/modal.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { IDLE_TIMEOUT_MS, IS_IOS, SELECTORS, STORAGE_NAMES } from '@root/app.config';
import { BlindData, ModalBlindData, Opening, Point, NodeRef } from '@root/app.interfaces';
import { BlindListModalComponent } from '@shared/modals/blind-list-modal/blind-list-modal.component';
import { debounceTime, filter, first, firstValueFrom, fromEvent, skip, Subscription, timer } from 'rxjs';
import { ComponentType } from '@angular/cdk/portal';

@Component({
	selector: 'app-opening-overlay',
	templateUrl: './opening-overlay.component.html',
	styleUrl: './opening-overlay.component.scss',
})
export class OpeningOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('overlaySVG', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

	@ViewChild('polygonLayer', { static: true, read: ElementRef }) polygonLayerRef!: ElementRef<SVGGElement>;
	@ViewChild('controlLayer', { static: true, read: ElementRef }) controlLayerRef!: ElementRef<SVGGElement>;

	private readonly CONTROL_CONTAINER_WIDTH = 220; // matches .polygon-button-container.width
	private readonly CONTROL_CONTAINER_OFFSET = 10;
	private readonly CONTROL_BUTTON_WIDTH = 34;
	private isCoarse = window.matchMedia('(pointer: coarse)').matches;
	private readonly HIT_RADIUS = this.isCoarse ? 22 : 18; // apple recommend 44px for touch
	private readonly NODE_RADIUS = 5;

	showTips = false;
	private tipsDisabled = false;

	private blindListData: BlindData[];
	openings: Opening[] = [];
	private savePointOpenings: Opening[] = [];
	private imageSize = { width: 0, height: 0 };
	private lastCanvasSize: { clientWidth: number; clientHeight: number } | null = null;

	private polygonMap = new Map<number, SVGPolygonElement>();
	private nodeMap = new Map<number, NodeRef[]>();
	private polygonControlMap = new Map<number, SVGForeignObjectElement>();
	private polygonLabelMap = new Map<number, SVGGElement>();

	private draggingPoint: { openingId: number; pointIndex: number } | null = null;
	private draggingPolygonId: number | null = null;
	private lastPtrPos: Point = { x: 0, y: 0 };
	private ptrDownPosition: Point | null = null; // used to check if it was a click or drag
	private activePointerId: number | null = null;
	private dragThreshold = 5;
	private hasDragged = false;

	private drawing = false;
	private drawStart: Point | null = null;
	private previewPolygon: SVGPolygonElement | null = null;
	private previewNodes: SVGCircleElement[] = [];
	private nextOpeningId = 0;

	private openPopupMenu: HTMLElement | null = null; // used to track and close open popup menus
	private openConfirmPopup: HTMLElement | null = null; // used to track and close open confirm popup

	private openingListeners = new Map<number, Record<string, () => void>>();
	private unlistenPtrMove: () => void;
	private unlistenPtrUp: () => void;
	private unlistenPtrCancel: () => void;
	private unlistenManualDrawMouseDown: () => void;
	private unlistenManualDrawMouseMove: () => void;
	private unlistenManualDrawMouseUp: () => void;
	private unlistenOutsideClick: () => void;

	private getSelectOpeningMode: Subscription;
	private getBlindUnselect: Subscription;
	private getOpeningDelete: Subscription;
	private tipsTimer: Subscription;
	private getIsAnOpeningSelected: Subscription;
	private windowResizeSubscription: Subscription;

	constructor(
		private changeDetection: ChangeDetectorRef,
		private renderer: Renderer2,
		private sessionStorageService: SessionStorageService,
		private shareService: ShareService,
		private dialog: MatDialog,
		private modalService: ModalService,
	) {}

	ngOnInit() {
		this.windowResizeSubscription?.unsubscribe();
		this.windowResizeHandler();
	}

	ngAfterViewInit(): void {
		this.renderer.setStyle(this.svgRef.nativeElement, 'touch-action', 'none');
		this.unlistenPtrMove = this.renderer.listen('document', 'pointermove', (event: PointerEvent) => this.onPtrMove(event));
		this.unlistenPtrUp = this.renderer.listen('document', 'pointerup', (event: PointerEvent) => this.onPointerUp(event));
		this.unlistenPtrCancel = this.renderer.listen('document', 'pointercancel', (event: PointerEvent) => this.onPointerCancel(event));
		this.unlistenOutsideClick = this.renderer.listen('document', 'click', (event: Event) => {
			const targetElement = event.target as HTMLElement;
			if (targetElement && targetElement.closest(SELECTORS.opening_confirm_popup)) {
				return;
			}

			if (this.openPopupMenu) {
				this.openPopupMenu.classList.add('hidden');
				this.openPopupMenu = null;
			}

			if (this.openConfirmPopup) {
				this.openConfirmPopup.classList.add('hidden');
				this.openConfirmPopup = null;
			}
		});

		this.unlistenManualDrawMouseDown = this.renderer.listen(this.svgRef.nativeElement, 'mousedown', (event: MouseEvent) => {
			if (event.target !== this.svgRef.nativeElement) {
				return;
			}
			this.onManualDrawStart(event);
		});

		this.getIsAnOpeningSelected = this.shareService.getIsAnOpeningSelected.subscribe((selected: boolean) => {
			if (selected) {
				this.stopTipsPermanently();
			}
		});

		this.refreshBlindListHandler();
		this.refreshOpeningsHandler();
	}

	ngOnDestroy(): void {
		this.sessionStorageService.removeData(STORAGE_NAMES.zip_openings_data);
		this.sessionStorageService.setSession(this.unScaleOpenings(this.openings), STORAGE_NAMES.zip_openings_data);

		if (this.unlistenPtrMove) {
			this.unlistenPtrMove();
		}
		if (this.unlistenPtrUp) {
			this.unlistenPtrUp();
		}
		if (this.unlistenPtrCancel) {
			this.unlistenPtrCancel();
		}
		if (this.unlistenOutsideClick) {
			this.unlistenOutsideClick();
		}
		if (this.unlistenManualDrawMouseDown) {
			this.unlistenManualDrawMouseDown();
		}

		this.openingListeners.forEach((listenerMap) => {
			Object.values(listenerMap).forEach((unlisten) => unlisten());
		});
		this.openingListeners.clear();
		this.getSelectOpeningMode.unsubscribe();
		this.getBlindUnselect.unsubscribe();
		this.getOpeningDelete.unsubscribe();
		this.tipsTimer?.unsubscribe();
		this.getIsAnOpeningSelected.unsubscribe();
		this.windowResizeSubscription?.unsubscribe();
	}

	windowResizeHandler(): void {
		this.windowResizeSubscription = fromEvent(window, 'resize')
			.pipe(debounceTime(50))
			.subscribe(this.updateOpeningsPositionOnResize.bind(this));
	}

	private updateOpeningsPositionOnResize(): void {
		const oldCanvas = this.lastCanvasSize;

		requestAnimationFrame(() => {
			const newCanvas = this.getCanvasSizeSnapshot();

			if (!oldCanvas || !newCanvas) {
				return;
			}

			this.openings.forEach((opening) => {
				opening.coordinates = this.rescaleCanvasCoordinates(
					oldCanvas,
					newCanvas,
					opening.coordinates,
					this.imageSize.width,
					this.imageSize.height,
				);
			});

			this.lastCanvasSize = newCanvas;
			this.openings.forEach((opening) => {
				opening.isFitted = false; // Reset due to Blinds shifts after screen rotation in scene IV.
				this.draw(opening);
				this.updateControlButtonPosition(opening);
				this.updateLabelPosition(opening);
			});
		});
	}

	private refreshBlindListHandler(): void {
		this.getSelectOpeningMode = this.shareService.getSelectOpeningMode.subscribe((selectOpeningMode: boolean) => {
			if (selectOpeningMode) {
				this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
				this.savePointOpenings = structuredClone(this.openings);
				this.resetTipsIdleTimer();

				if (this.openings.some((opening) => opening.isSelected === true)) {
					// Kind of tacked on but works for now
					this.shareService.setIsAnOpeningSelected(true);
				}
			}
		});
	}

	private refreshOpeningsHandler(): void {
		// based on the assumption setBlindUnselect is only called when a blind is deleted
		this.getBlindUnselect = this.shareService.getBlindUnselect.subscribe(() => {
			this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
			const blindListIds = this.blindListData?.map((b) => b.blind_id);

			// TODO: functionlise this
			this.openings.forEach((opening) => {
				if (opening.blindId && !blindListIds?.includes(opening.blindId)) {
					opening.isSelected = false; // TODO: functionlise this
					opening.isFitted = false;
					opening.blindId = null;
					opening.blindType = null;
					opening.displayNumber = null;
					opening.name = null;

					this.applyPolygonSelectionStyle(opening);

					const controlDiv = this.polygonControlMap.get(opening.id)?.firstChild as HTMLDivElement;
					if (controlDiv) {
						this.updateControlButtonContent(opening, controlDiv);
					}
				}
			});

			this.reorderDisplayNumbers();
		});

		this.getOpeningDelete = this.shareService.getOpeningDeselect.subscribe((openingId) => {
			// Deselect the opening if no created blind has been deleted.
			const opening = this.openings.find((o) => o.id === openingId); // TODO: functionlise this
			opening.isSelected = false;
			opening.isFitted = false;
			opening.blindId = null;
			opening.blindType = null;
			opening.displayNumber = null;
			opening.name = null;

			this.applyPolygonSelectionStyle(opening);

			const controlDiv = this.polygonControlMap.get(opening.id)?.firstChild as HTMLDivElement;
			if (controlDiv) {
				this.updateControlButtonContent(opening, controlDiv);
			}

			this.reorderDisplayNumbers();
		});
	}

	private getCanvasSizeSnapshot(): { clientWidth: number; clientHeight: number } | null {
		const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
		if (!canvas) {
			return null;
		}

		return {
			clientWidth: canvas.clientWidth,
			clientHeight: canvas.clientHeight,
		};
	}

	public initOpenings(keepSelected = false): void {
		const oldOpenings = this.sessionStorageService.getSession(STORAGE_NAMES.zip_openings_data);
		if (oldOpenings) {
			// Delays creating openings until DOM is ready and BBox won't return 0
			this.shareService.getSelectOpeningMode.pipe(filter(Boolean), first()).subscribe(() => {
				this.lastCanvasSize = this.getCanvasSizeSnapshot();
				const detectionResults = this.sessionStorageService.getDetectionResults(STORAGE_NAMES.zip_detection_results);
				this.imageSize = detectionResults.imageSize;

				this.openings = this.scaleOpenings(oldOpenings);

				this.clearSvg();
				this.openings.forEach((opening) => {
					this.createInteractivePolygon(opening);
					this.applyPolygonSelectionStyle(opening);
					this.nextOpeningId = opening.id > this.nextOpeningId ? opening.id : this.nextOpeningId;
				});
				this.nextOpeningId++;

				this.reorderDisplayNumbers();

				if (this.openings.some((opening) => opening.isSelected === true)) {
					this.shareService.setIsAnOpeningSelected(true);
				} else {
					this.tipsDisabled = false;
				}
			});
		} else {
			// Protection for if the detectionResult is returned before select-opening-screen has been made visible and therefore the canvas hasn't been resized yet
			this.shareService.getSelectOpeningMode.pipe(filter(Boolean), first()).subscribe(() => {
				// Predicated on the fact that the canvas is only resized by select-opening-screen becoming visible and nothing else, in the aforementioned case
				requestAnimationFrame(() => {
					const selectedOpenings = keepSelected ? this.openings.filter((o) => o.isSelected) : [];

					this.loadAndScaleOpenings();

					selectedOpenings.forEach((opening) => {
						opening.id = this.nextOpeningId++;
						this.openings.push(opening);
					});

					this.createPolygons();

					if (keepSelected) {
						this.openings.forEach((opening) => this.applyPolygonSelectionStyle(opening));
						if (selectedOpenings.length > 0) {
							this.shareService.setIsAnOpeningSelected(true);
						}
					} else {
						this.tipsDisabled = false;
					}
				});
			});
		}

		this.savePointOpenings = structuredClone(this.openings);
		this.resetTipsIdleTimer();
	}

	// TODO: probably don't need this and just put a check in loadAndScaleOpenings so it does trip map or whatever was tripping.
	public initWithoutOpenings(): void {
		this.openings = [];
		this.savePointOpenings = structuredClone(this.openings);
		this.clearSvg();
		this.shareService.setIsAnOpeningSelected(false);
		this.lastCanvasSize = this.getCanvasSizeSnapshot();
		const detectionResults = this.sessionStorageService.getDetectionResults(STORAGE_NAMES.zip_detection_results);
		this.imageSize = detectionResults.imageSize;
		this.tipsDisabled = false;
		this.resetTipsIdleTimer();
	}

	private loadAndScaleOpenings(): void {
		this.lastCanvasSize = this.getCanvasSizeSnapshot();

		const detectionResults = this.sessionStorageService.getDetectionResults(STORAGE_NAMES.zip_detection_results);
		const { imageSize = { width: 0, height: 0 }, results = [] } = detectionResults || {};
		this.imageSize = imageSize;

		this.nextOpeningId = 0; // Reset the ID counter

		this.openings = results.map((quad: Point[], index: number) => {
			const scaledCoords = this.scaleCoordinates(imageSize.width, imageSize.height, this.lastCanvasSize, quad);
			this.nextOpeningId = index + 1; // Increment the ID counter for the next opening
			return {
				id: index,
				coordinates: scaledCoords,
				isSelected: false,
				isFitted: false,
				blindId: null,
				displayNumber: null,
				name: null,
				blindType: null,
			};
		});
	}

	private scaleCoordinates(originalWidth: number, originalHeight: number, canvasSize, originalCoords: Point[]): Point[] {
		const canvasWidth = canvasSize.clientWidth;
		const canvasHeight = canvasSize.clientHeight;

		const canvasAspectRatio = canvasWidth / canvasHeight;
		const imageAspectRatio = originalWidth / originalHeight;

		let scaleX,
			scaleY,
			offsetX = 0,
			offsetY = 0;

		if (imageAspectRatio > canvasAspectRatio) {
			scaleX = canvasWidth / originalWidth;
			scaleY = scaleX;
			offsetY = (canvasHeight - originalHeight * scaleY) / 2;
		} else {
			scaleY = canvasHeight / originalHeight;
			scaleX = scaleY;
			offsetX = (canvasWidth - originalWidth * scaleX) / 2;
		}

		const scaledCoords = originalCoords.map(({ x, y }) => ({
			x: x * scaleX + offsetX,
			y: y * scaleY + offsetY,
		}));

		return scaledCoords;
	}

	private rescaleCanvasCoordinates(oldCanvas, newCanvas, coords, originalImageWidth, originalImageHeight) {
		const getScaleParams = (canvas) => {
			const canvasWidth = canvas.clientWidth;
			const canvasHeight = canvas.clientHeight;

			const canvasAspectRatio = canvasWidth / canvasHeight;
			const imageAspectRatio = originalImageWidth / originalImageHeight;

			let scale,
				offsetX = 0,
				offsetY = 0;

			if (imageAspectRatio > canvasAspectRatio) {
				scale = canvasWidth / originalImageWidth;
				offsetY = (canvasHeight - originalImageHeight * scale) / 2;
			} else {
				scale = canvasHeight / originalImageHeight;
				offsetX = (canvasWidth - originalImageWidth * scale) / 2;
			}

			return { scale, offsetX, offsetY };
		};

		const { scale: oldScale, offsetX: oldOffsetX, offsetY: oldOffsetY } = getScaleParams(oldCanvas);
		const { scale: newScale, offsetX: newOffsetX, offsetY: newOffsetY } = getScaleParams(newCanvas);

		const rescaledCoords = coords.map(({ x, y }) => {
			// convert from canvas space back to normalized image space
			const imgX = (x - oldOffsetX) / oldScale;
			const imgY = (y - oldOffsetY) / oldScale;

			// convert from normalized image space to new canvas space
			const newX = imgX * newScale + newOffsetX;
			const newY = imgY * newScale + newOffsetY;

			return { x: newX, y: newY };
		});

		return rescaledCoords;
	}

	private unScaleCoordinates(originalWidth: number, originalHeight: number, canvasSize, canvasCoords: Point[]): Point[] {
		const canvasWidth = canvasSize.clientWidth;
		const canvasHeight = canvasSize.clientHeight;

		const canvasAspectRatio = canvasWidth / canvasHeight;
		const imageAspectRatio = originalWidth / originalHeight;

		let scaleX: number,
			scaleY: number,
			offsetX = 0,
			offsetY = 0;

		if (imageAspectRatio > canvasAspectRatio) {
			scaleX = canvasWidth / originalWidth;
			scaleY = scaleX;
			offsetY = (canvasHeight - originalHeight * scaleY) / 2;
		} else {
			scaleY = canvasHeight / originalHeight;
			scaleX = scaleY;
			offsetX = (canvasWidth - originalWidth * scaleX) / 2;
		}

		const unScaledCoords = canvasCoords.map(({ x, y }) => ({
			x: (x - offsetX) / scaleX,
			y: (y - offsetY) / scaleY,
		}));

		return unScaledCoords;
	}

	private unScaleOpenings(openings: Opening[]): Opening[] {
		openings.forEach((opening) => {
			opening.coordinates = this.unScaleCoordinates(this.imageSize.width, this.imageSize.height, this.lastCanvasSize, opening.coordinates);
		});

		return openings;
	}

	private scaleOpenings(openings: Opening[]): Opening[] {
		openings.forEach((opening) => {
			opening.coordinates = this.scaleCoordinates(this.imageSize.width, this.imageSize.height, this.lastCanvasSize, opening.coordinates);
		});

		return openings;
	}

	private createPolygons(): void {
		this.shareService.setIsAnOpeningSelected(false);
		this.clearSvg();
		this.openings.forEach((opening) => this.createInteractivePolygon(opening));
	}

	private clearSvg(): void {
		const polygonLayer = this.polygonLayerRef.nativeElement;
		while (polygonLayer.firstChild) {
			polygonLayer.removeChild(polygonLayer.firstChild);
		}

		const controlLayer = this.controlLayerRef.nativeElement;
		while (controlLayer.firstChild) {
			controlLayer.removeChild(controlLayer.firstChild);
		}

		this.polygonMap.clear();
		this.nodeMap.clear();
		this.polygonControlMap.clear();
		this.polygonLabelMap.clear();
		this.openingListeners.forEach((listenerMap) => {
			Object.values(listenerMap).forEach((unlisten) => unlisten());
		});
		this.openingListeners.clear();
	}

	private resetTipsIdleTimer(): void {
		if (this.tipsDisabled) {
			return;
		}

		this.tipsTimer?.unsubscribe();
		this.showTips = false;

		this.tipsTimer = timer(IDLE_TIMEOUT_MS).subscribe(() => {
			this.showTips = true;
			this.changeDetection.markForCheck;
		});
	}

	private stopTipsPermanently(): void {
		this.tipsDisabled = true;
		this.showTips = false;
		this.tipsTimer?.unsubscribe();
		this.tipsTimer = undefined;
	}

	// This is now unused but could maybe be useful in the future
	private hidePolygonById(id: number): void {
		// Technically should hide label too but openings aren't hidden when selected which is the only time the lable is visible
		const polygon = this.polygonMap.get(id);
		const nodeRefs = this.nodeMap.get(id);
		const controls = this.polygonControlMap.get(id);

		if (polygon) {
			this.renderer.setStyle(polygon, 'display', 'none');
		}

		if (nodeRefs) {
			nodeRefs.forEach((ref) => {
				this.renderer.setStyle(ref.group, 'display', 'none');
			});
		}

		if (controls) {
			this.renderer.setStyle(controls, 'display', 'none');
		}
	}

	// This is now unused but could maybe be useful in the future
	private showPolygonById(id: number): void {
		// Technically should show label too but openings aren't hidden when selected which is the only time the lable is visible
		const polygon = this.polygonMap.get(id);
		const nodeRefs = this.nodeMap.get(id);
		const controls = this.polygonControlMap.get(id);

		if (polygon) {
			this.renderer.removeStyle(polygon, 'display');
		}

		if (nodeRefs) {
			nodeRefs.forEach((ref) => {
				this.renderer.removeStyle(ref.group, 'display');
			});
		}

		if (controls) {
			this.renderer.removeStyle(controls, 'display');
		}
	}

	private createInteractivePolygon(opening: Opening): void {
		// I tried using the proper angular way of doing this with the template etc. but it had horrible performance issues
		const polygonLayer = this.polygonLayerRef.nativeElement;
		const id = opening.id;
		const listeners: Record<string, () => void> = {};

		const polygon = this.renderer.createElement('polygon', 'svg');
		this.renderer.setAttribute(polygon, 'id', `quad-${id}`);
		this.renderer.addClass(polygon, 'custom-polygon');
		listeners.polygonPtrDown = this.renderer.listen(polygon, 'pointerdown', (e) => {
			e.stopPropagation();
			this.onPolygonPtrDown(e, id);
		});
		this.renderer.appendChild(polygonLayer, polygon);
		this.polygonMap.set(id, polygon);

		const nodeRefs: NodeRef[] = [];
		opening.coordinates.forEach((pt, i) => {
			const g = this.renderer.createElement('g', 'svg');
			this.renderer.setAttribute(g, 'id', `node-wrap-${id}-${i}`);
			this.renderer.addClass(g, 'node-group');

			const hit = this.renderer.createElement('circle', 'svg');
			this.renderer.setAttribute(hit, 'id', `node-hit-${id}-${i}`);
			this.renderer.setAttribute(hit, 'r', this.HIT_RADIUS.toString());
			this.renderer.setStyle(hit, 'fill', 'transparent');
			this.renderer.setStyle(hit, 'pointer-events', 'all');

			const dot = this.renderer.createElement('circle', 'svg');
			this.renderer.setAttribute(dot, 'id', `node-dot-${id}-${i}`);
			this.renderer.setAttribute(dot, 'r', this.NODE_RADIUS.toString());
			this.renderer.addClass(dot, 'custom-node');

			listeners[`node${i}PtrDown`] = this.renderer.listen(g, 'pointerdown', (e) => {
				e.stopPropagation();
				this.onNodePtrDown(e, id, i);
			});

			this.renderer.appendChild(g, hit);
			this.renderer.appendChild(g, dot);
			this.renderer.appendChild(polygonLayer, g);
			nodeRefs.push({ group: g, hit, dot });
		});
		this.nodeMap.set(id, nodeRefs);

		const labelGroup = this.renderer.createElement('g', 'svg'); // TODO: could make a addLabel function
		this.renderer.addClass(labelGroup, SELECTORS.polygon_label_group);

		const background = this.renderer.createElement('rect', 'svg');
		this.renderer.addClass(background, SELECTORS.polygon_label_background);
		this.renderer.insertBefore(labelGroup, background, labelGroup.firstChild);

		const labelCircle = this.renderer.createElement('circle', 'svg');
		this.renderer.addClass(labelCircle, SELECTORS.polygon_label_circle);

		const labelNumber = this.renderer.createElement('text', 'svg');
		this.renderer.addClass(labelNumber, SELECTORS.polygon_label_number);
		this.renderer.setAttribute(labelNumber, 'x', '0');
		this.renderer.setAttribute(labelNumber, 'y', '1');

		const labelText = this.renderer.createElement('text', 'svg');
		this.renderer.addClass(labelText, SELECTORS.polygon_label_text);
		this.renderer.setAttribute(labelText, 'x', '0');
		this.renderer.setAttribute(labelText, 'y', '1');

		listeners.labelClick = this.renderer.listen(labelGroup, 'click', () => {
			this.handleLabelClick(opening);
			this.resetTipsIdleTimer();
		});

		labelGroup.appendChild(labelCircle);
		labelGroup.appendChild(labelNumber);
		labelGroup.appendChild(labelText);

		this.renderer.appendChild(polygonLayer, labelGroup);
		this.polygonLabelMap.set(id, labelGroup);

		this.addControlButtons(opening);

		this.draw(opening);

		this.openingListeners.set(id, listeners);
	}

	private handleLabelClick(opening: Opening): void {
		// Count the number of no created blinds. Sometimes for some reason blindType is null after deletion, so I added a check opening.blindType
		const placeholderBlinds = this.openings.filter((opening) => !opening.blindId && opening.isSelected && opening.blindType);
		const openingBlindIds = Object.fromEntries(this.openings.filter((o) => o.blindId).map((o) => [o.blindId, o.id]));

		// If there is only one element in blindListData do not open a modal window
		if (this.blindListData.length + placeholderBlinds.length === 1) {
			return;
		}

		const blindListModal = this.dialog.open(BlindListModalComponent, {
			...this.modalService.getConfig(BlindListModalComponent.ID),
			data: { opening, placeholderBlinds, openingBlindIds },
		});

		blindListModal.afterClosed().subscribe((result: ModalBlindData | undefined) => {
			if (!result) {
				return;
			}

			const existing = this.openings.find((o) => o.id === result.openingId);

			if (existing && existing !== opening) {
				existing.isSelected = false;
				existing.isFitted = false;
				existing.blindId = null;
				existing.blindType = null;
				existing.displayNumber = null;
				existing.name = null;

				this.applyPolygonSelectionStyle(existing);

				const controlDiv = this.polygonControlMap.get(existing.id)?.firstChild as HTMLDivElement; // TODO: updateControlButtonContent should deal with getting the controlDiv instead of having to pass it in.
				if (controlDiv) {
					this.updateControlButtonContent(existing, controlDiv);
				}
			}

			opening.isSelected = true;
			opening.isFitted = opening.blindId === result.blindId ? opening.isFitted : false; // If it is the same blind, keep the isFitted value, else it is no longer fitted
			opening.blindId = result.blindId;
			opening.blindType = result.type;
			opening.displayNumber = result.displayNumber;
			opening.name = result.name;

			this.positionPolygonLabel(opening, this.polygonLabelMap.get(opening.id));
			this.reorderDisplayNumbers();
		});
	}

	private positionPolygonLabel(opening: Opening, group: SVGGElement): void {
		// Positions the polygon label on its center and size.
		// Moved to a separate function to allow re-rendering after model selection via BlindListModalComponent.

		const center = this.getPolygonCenter(opening.coordinates);

		requestAnimationFrame(() => {
			const bgRect = group.querySelector(`.${SELECTORS.polygon_label_background}`) as SVGRectElement;
			this.renderer.setAttribute(bgRect, 'display', 'none');

			const bbox = group.getBBox();

			const paddingX = 8;
			const paddingY = 6;

			this.renderer.setAttribute(bgRect, 'x', (bbox.x - paddingX + 2).toString()); // TODO: fix magic number
			this.renderer.setAttribute(bgRect, 'y', (bbox.y - paddingY).toString());
			this.renderer.setAttribute(bgRect, 'width', (bbox.width + paddingX * 2).toString());
			this.renderer.setAttribute(bgRect, 'height', (bbox.height + paddingY * 2).toString());
			this.renderer.setAttribute(bgRect, 'rx', '5');

			this.renderer.removeAttribute(bgRect, 'display');

			const offsetX = -bbox.width / 2;
			const offsetY = -bbox.height / 2;
			this.renderer.setAttribute(group, 'transform', `translate(${center.x + offsetX}, ${center.y + offsetY})`);
		});
	}

	private addControlButtons(opening: Opening): void {
		const controlLayer = this.controlLayerRef.nativeElement;
		const id = opening.id;

		const foreignObject = this.renderer.createElement('foreignObject', 'svg');
		const position = this.getControlButtonPosition(opening.coordinates);
		this.renderer.setAttribute(foreignObject, 'x', position.x.toString()); // width
		this.renderer.setAttribute(foreignObject, 'y', position.y.toString()); // height
		this.renderer.addClass(foreignObject, 'polygon-button-container');
		this.renderer[position.shouldOffset ? 'removeClass' : 'addClass'](foreignObject, 'offset');

		const controlDiv = document.createElement('div');
		controlDiv.className = 'polygon-control';
		controlDiv.setAttribute('data-opening-id', id.toString());
		this.updateControlButtonContent(opening, controlDiv);

		foreignObject.appendChild(controlDiv);
		controlLayer.appendChild(foreignObject);
		this.polygonControlMap.set(id, foreignObject);
	}

	private draw(opening: Opening): void {
		const polygon = this.polygonMap.get(opening.id);
		const nodeRefs = this.nodeMap.get(opening.id);

		if (!polygon || !nodeRefs) {
			return;
		}

		const pointStr = opening.coordinates.map((p) => `${p.x},${p.y}`).join(' ');
		polygon.setAttribute('points', pointStr);

		opening.coordinates.forEach((pt, i) => {
			nodeRefs[i].dot.setAttribute('cx', pt.x.toString());
			nodeRefs[i].dot.setAttribute('cy', pt.y.toString());
			nodeRefs[i].hit.setAttribute('cx', pt.x.toString());
			nodeRefs[i].hit.setAttribute('cy', pt.y.toString());
		});
	}

	private getControlButtonPosition(coordinates: Point[]): { x: number; y: number; shouldOffset: boolean } {
		const topRight = this.getVisualTopRight(coordinates);
		const shouldOffset = topRight.x >= this.CONTROL_CONTAINER_WIDTH + this.CONTROL_CONTAINER_OFFSET;
		const XPositionOffset = shouldOffset ? (IS_IOS() ? 0 : this.CONTROL_CONTAINER_WIDTH) : this.CONTROL_BUTTON_WIDTH;

		return {
			x: topRight.x - this.CONTROL_CONTAINER_OFFSET - XPositionOffset,
			y: topRight.y + this.CONTROL_CONTAINER_OFFSET,
			shouldOffset,
		};
	}

	private getVisualTopRight(points: { x: number; y: number }[]): { x: number; y: number } {
		const sortedByX = [...points].sort((a, b) => b.x - a.x); // sort by x descending
		const rightMost = sortedByX.slice(0, 2); // two right most points
		return rightMost.reduce((best, pt) => (pt.y < best.y ? pt : best)); // return one with smallest y
	}

	private updateControlButtonContent(opening: Opening, controlDiv: HTMLDivElement): void {
		this.clearOpeningControlListeners(opening.id);
		controlDiv.innerHTML = '';

		const existingListeners = this.openingListeners.get(opening.id) ?? {};

		const confirmPopup = this.createControlConfirmPopup(opening, existingListeners);
		controlDiv.appendChild(confirmPopup);

		if (opening.isSelected) {
			const menuWrapper = document.createElement('div');
			menuWrapper.className = 'popup-menu-wrapper hidden';

			const deselectOption = document.createElement('div');
			deselectOption.className = 'popup-option';
			deselectOption.innerHTML = `<span class="popup-icon icon-deselect"></span> <span>Deselect</span>`;
			existingListeners.deselectClick = this.renderer.listen(deselectOption, 'click', () => {
				// TODO: maybe extract to a function
				opening.isSelected = false;
				this.applyPolygonSelectionStyle(opening);
				this.updateControlButtonContent(opening, controlDiv);
				opening.blindId = null;
				opening.blindType = null;
				opening.displayNumber = null;
				opening.name = null;
				this.reorderDisplayNumbers();
				opening.isFitted = false;
			});

			const deleteOption = document.createElement('div');
			deleteOption.className = 'popup-option';
			deleteOption.innerHTML = `<span class="popup-icon icon-delete"></span>Delete`;
			existingListeners.deleteClick = this.renderer.listen(deleteOption, 'click', (event) => {
				// The logic for deleting an opening has been moved to the confirmation popup.
				event.stopPropagation();

				if (this.openConfirmPopup) {
					this.openConfirmPopup.classList.add('hidden');
					this.openConfirmPopup = null;
				}

				confirmPopup.classList.remove('hidden');
				this.openConfirmPopup = confirmPopup;
			});

			menuWrapper.appendChild(deselectOption);
			menuWrapper.appendChild(deleteOption);
			controlDiv.appendChild(menuWrapper);

			const editBtn = document.createElement('div');
			editBtn.className = 'polygon-control-button';
			editBtn.innerHTML = `<div class="edit-button-icon"></div>`;
			editBtn.title = 'Edit opening';
			existingListeners.editClick = this.renderer.listen(editBtn, 'click', (event) => {
				// TODO: maybe change to listen on controlDiv instead of editBtn as editBtn is smaller than the controlDiv
				event.stopPropagation();
				const wasOpen = !menuWrapper.classList.contains('hidden');

				if (this.openPopupMenu && this.openPopupMenu !== menuWrapper) {
					this.openPopupMenu.classList.add('hidden'); // close others
				}

				if (this.openConfirmPopup) {
					this.openConfirmPopup.classList.add('hidden');
					this.openConfirmPopup = null;
				}

				if (wasOpen) {
					// toggle current
					menuWrapper.classList.add('hidden');
					this.openPopupMenu = null;
				} else {
					menuWrapper.classList.remove('hidden');
					this.openPopupMenu = menuWrapper;
				}
			});
			controlDiv.appendChild(editBtn);
		} else {
			const deleteBtn = document.createElement('div');
			deleteBtn.className = 'polygon-control-button';
			deleteBtn.innerHTML = `<div class="delete-button-icon"></div>`;
			deleteBtn.title = 'Delete opening';
			existingListeners.deleteClick = this.renderer.listen(deleteBtn, 'click', () => {
				// TODO: maybe change to listen on controlDiv instead of deleteBtn as deleteBtn is smaller than the controlDiv
				this.deleteOpeningById(opening.id);
				this.reorderDisplayNumbers();
				this.resetTipsIdleTimer();
			});
			controlDiv.appendChild(deleteBtn);
		}

		this.openingListeners.set(opening.id, existingListeners);
	}

	private createControlConfirmPopup(opening: Opening, existingListeners: Record<string, () => void>): HTMLElement {
		// This function creates a confirmation popup for deleting an opening.
		const confirmPopup = document.createElement('div');
		confirmPopup.innerHTML = '<span class="popup-text">Are you sure you want to delete this opening?</span>';
		confirmPopup.className = 'popup-confirm hidden';

		const confirmButton = document.createElement('div');
		confirmButton.className = 'popup-button yellow';
		confirmButton.innerHTML = 'Yes';
		existingListeners.confirmDelete = this.renderer.listen(confirmButton, 'click', () => {
			// The logic for deleting an opening is now located here.
			this.deleteOpeningById(opening.id);
			this.reorderDisplayNumbers();
		});

		const closeButton = document.createElement('div');
		closeButton.className = 'popup-button';
		closeButton.innerHTML = 'No';
		existingListeners.closeConfirm = this.renderer.listen(closeButton, 'click', (event) => {
			event.stopPropagation();
			confirmPopup.classList.add('hidden');
			this.openConfirmPopup = null;
		});

		confirmPopup.appendChild(confirmButton);
		confirmPopup.appendChild(closeButton);

		return confirmPopup;
	}

	private clearOpeningControlListeners(id: number): void {
		// TODO: Add confirm popup listeners
		const keysToRemove = ['editClick', 'deleteClick', 'deselectClick'];
		const listeners = this.openingListeners.get(id);
		if (!listeners) {
			return;
		}

		keysToRemove.forEach((key) => {
			if (listeners[key]) {
				listeners[key]!();
				delete listeners[key];
			}
		});
	}

	private reorderDisplayNumbers(): void {
		const selectedOpenings = this.openings.filter((o) => o.isSelected).sort((a, b) => (a.displayNumber ?? 0) - (b.displayNumber ?? 0)); // keep the original order
		if (selectedOpenings.length === 0) {
			this.shareService.setIsAnOpeningSelected(false);
			return;
		}

		this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		selectedOpenings.forEach((opening, index) => {
			if (!opening.blindId) {
				this.assignBlindToOpening(opening, index + 1);
			} else {
				const blindListIndex = this.blindListData.findIndex((b) => b.blind_id === opening.blindId);
				opening.name = this.blindListData[blindListIndex].name;
				opening.displayNumber = blindListIndex + 1;
			}

			const group = this.polygonLabelMap.get(opening.id);
			if (group) {
				const labelNumber = group.querySelector(`.${SELECTORS.polygon_label_number}`) as SVGTextElement;
				const labelText = group.querySelector(`.${SELECTORS.polygon_label_text}`) as SVGTextElement;
				const labelCircle = group.querySelector(`.${SELECTORS.polygon_label_circle}`) as SVGCircleElement;

				if (labelNumber) {
					labelNumber.textContent = opening.displayNumber.toString();
				}
				if (labelText) {
					labelText.textContent = opening.name;
				}

				const groupVariant =
					opening.blindType === 'outdoor' ? SELECTORS.polygon_label_group_outdoor : SELECTORS.polygon_label_group_interior;
				const circleVariant =
					opening.blindType === 'outdoor' ? SELECTORS.polygon_label_circle_outdoor : SELECTORS.polygon_label_circle_interior;

				this.renderer.removeClass(group, SELECTORS.polygon_label_group_outdoor);
				this.renderer.removeClass(group, SELECTORS.polygon_label_group_interior);
				this.renderer.removeClass(labelCircle, SELECTORS.polygon_label_circle_outdoor);
				this.renderer.removeClass(labelCircle, SELECTORS.polygon_label_circle_interior);

				this.renderer.addClass(group, groupVariant);
				this.renderer.addClass(labelCircle, circleVariant);

				// this.updateLabelPosition(opening); // hasn't been needed so far but more testing needed
			}
		});
	}

	private assignBlindToOpening(opening: Opening, index: number): void {
		this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const usedBlindIds = new Set(this.openings.map((o) => o.blindId).filter((id) => id !== null));
		const currentType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);

		const availableBlindIndex = this.blindListData.findIndex((b) => b.type === currentType && !usedBlindIds.has(b.blind_id));

		const availableBlind = availableBlindIndex !== -1 ? this.blindListData[availableBlindIndex] : undefined;

		if (availableBlind) {
			opening.blindId = availableBlind.blind_id;
			opening.blindType = availableBlind.type;
			opening.displayNumber = availableBlindIndex + 1;
			opening.name = availableBlind.name;
		} else {
			opening.blindType = currentType;
			const blindCount = this.blindListData.length;
			opening.displayNumber = index > blindCount ? index : blindCount + 1;
			opening.name = `Blind ${opening.displayNumber}`;
		}
	}

	private async userAssignBlindToOpening(opening: Opening): Promise<void> {
		// set back to false because if delete or close are pressed on modal it should act like it is unselected
		opening.isSelected = false;

		const result = await this.openDialogWithDelay<BlindListModalComponent, object, ModalBlindData | undefined>(
			this.dialog,
			BlindListModalComponent,
			{
				...this.modalService.getConfig(BlindListModalComponent.ID),
				data: {
					opening,
					usedIds: this.openings.map((o) => o.blindId).filter((id) => id !== null),
				},
			},
		);

		if (!result) {
			return;
		}

		opening.isSelected = true;
		opening.blindId = result.blindId;
		opening.blindType = result.type;
		opening.displayNumber = result.displayNumber;
		opening.name = result.name;
	}

	private deleteOpeningById(id: number): void {
		const listeners = this.openingListeners.get(id);
		if (listeners) {
			Object.values(listeners).forEach((unlisten) => unlisten());
			this.openingListeners.delete(id);
		}

		this.polygonMap.get(id)?.remove();
		this.nodeMap.get(id)?.forEach((ref) => this.renderer.removeChild(this.polygonLayerRef.nativeElement, ref.group));
		this.polygonControlMap.get(id)?.remove();
		this.polygonLabelMap.get(id)?.remove();

		this.polygonMap.delete(id);
		this.nodeMap.delete(id);
		this.polygonControlMap.delete(id);
		this.polygonLabelMap.delete(id);

		this.openings = this.openings.filter((o) => o.id !== id);
	}

	private onPolygonPtrDown = (event: PointerEvent, id: number): void => {
		if (event.button !== 0) {
			return;
		}

		this.activePointerId = event.pointerId;
		(event.target as HTMLElement).setPointerCapture(event.pointerId);

		const svgRect = this.svgRef.nativeElement.getBoundingClientRect();
		this.lastPtrPos = {
			x: event.clientX - svgRect.left,
			y: event.clientY - svgRect.top,
		};
		this.ptrDownPosition = this.lastPtrPos;
		this.draggingPolygonId = id;
		this.hasDragged = false;
		document.body.style.cursor = 'move';
		event.preventDefault();
		this.resetTipsIdleTimer();
	};

	private onNodePtrDown = (event: PointerEvent, openingId: number, pointIndex: number): void => {
		if (event.button !== 0) {
			return;
		}

		this.activePointerId = event.pointerId;
		(event.target as HTMLElement).setPointerCapture(event.pointerId);
		this.draggingPoint = { openingId, pointIndex };
		document.body.style.cursor = 'grabbing';
		event.preventDefault();
		this.resetTipsIdleTimer();
	};

	private onPtrMove = (event: PointerEvent): void => {
		if (event.pointerId !== this.activePointerId) {
			return;
		}

		const svgRect = this.svgRef.nativeElement.getBoundingClientRect();
		const x = event.clientX - svgRect.left;
		const y = event.clientY - svgRect.top;

		if (this.draggingPoint) {
			const opening = this.openings.find((o) => o.id === this.draggingPoint!.openingId);
			if (!opening) {
				return;
			}

			opening.coordinates[this.draggingPoint.pointIndex] = { x, y };
			this.draw(opening);
			this.updateControlButtonPosition(opening);
			this.updateLabelPosition(opening);
			opening.isFitted = false;
			this.resetTipsIdleTimer();
		} else if (this.draggingPolygonId !== null) {
			const opening = this.openings.find((o) => o.id === this.draggingPolygonId);
			if (!opening) {
				return;
			}

			if (!this.hasDragged) {
				const dx = x - this.ptrDownPosition!.x;
				const dy = y - this.ptrDownPosition!.y;
				if (Math.hypot(dx, dy) > this.dragThreshold) {
					this.hasDragged = true;
				}
			}

			const dx = x - this.lastPtrPos.x;
			const dy = y - this.lastPtrPos.y;

			opening.coordinates.forEach((pt) => {
				pt.x += dx;
				pt.y += dy;
			});
			this.lastPtrPos = { x, y };
			this.draw(opening);
			this.updateControlButtonPosition(opening);
			this.updateLabelPosition(opening);
			opening.isFitted = false;
			this.resetTipsIdleTimer();
		}
	};

	private updateControlButtonPosition(opening: Opening): void {
		const control = this.polygonControlMap.get(opening.id);
		if (!control) {
			return;
		}

		const position = this.getControlButtonPosition(opening.coordinates);
		control.setAttribute('x', position.x.toString());
		control.setAttribute('y', position.y.toString());

		this.renderer[position.shouldOffset ? 'removeClass' : 'addClass'](control, 'offset');
	}

	private updateLabelPosition(opening: Opening): void {
		const group = this.polygonLabelMap.get(opening.id);
		if (!group || !opening.isSelected || !opening.displayNumber) {
			return;
		}

		const center = this.getPolygonCenter(opening.coordinates);
		requestAnimationFrame(() => {
			const bgRect = group.querySelector(`.${SELECTORS.polygon_label_background}`) as SVGRectElement;
			this.renderer.setAttribute(bgRect, 'display', 'none');

			const bbox = group.getBBox();

			this.renderer.removeAttribute(bgRect, 'display');

			const offsetX = -bbox.width / 2;
			const offsetY = -bbox.height / 2;

			this.renderer.setAttribute(group, 'transform', `translate(${center.x + offsetX}, ${center.y + offsetY})`);
		});
	}

	getPolygonCenter(points: Point[]): Point {
		const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
		const len = points.length;
		return { x: sum.x / len, y: sum.y / len };
	}

	private onPointerUp = (event: PointerEvent): void => {
		if (event.pointerId !== this.activePointerId) {
			return;
		}
		this.finishPointer(event, true);
	};

	private onPointerCancel = (event: PointerEvent): void => {
		if (event.pointerId !== this.activePointerId) {
			return;
		}
		this.finishPointer(event, false);
	};

	private finishPointer = (event: PointerEvent, maybeClick = false): void => {
		this.activePointerId = null;

		if (maybeClick && this.draggingPolygonId !== null && !this.hasDragged) {
			// Stop propagation to provent the dialog to be closed autoly
			event.stopPropagation();

			this.onPolygonClick(this.draggingPolygonId);
		}

		if (this.draggingPolygonId !== null || this.draggingPoint !== null) {
			this.resetTipsIdleTimer();
		}

		this.draggingPoint = null;
		this.draggingPolygonId = null;
		this.ptrDownPosition = null;
		document.body.style.cursor = 'default';
	};

	private async onPolygonClick(id: number): Promise<void> {
		const opening = this.openings.find((o) => o.id === id);
		if (!opening || opening.isSelected) {
			return;
		}

		opening.isSelected = true;

		const selectedCount = this.openings.filter((o) => o.isSelected).length;
		if (this.blindListData.length > selectedCount) {
			// If there is more than one blind available
			await this.userAssignBlindToOpening(opening);
		} else {
			this.assignBlindToOpening(opening, selectedCount);
		}

		this.applyPolygonSelectionStyle(opening);
		const controlDiv = this.polygonControlMap.get(opening.id)?.firstChild as HTMLDivElement;
		if (controlDiv) {
			this.updateControlButtonContent(opening, controlDiv);
		}

		this.shareService.setIsAnOpeningSelected(opening.isSelected);
	}

	private applyPolygonSelectionStyle(opening: Opening): void {
		const polygon = this.polygonMap.get(opening.id);
		const nodeRefs = this.nodeMap.get(opening.id);

		if (polygon) {
			if (opening.isSelected) {
				this.renderer.addClass(polygon, 'polygon-selected');
			} else {
				this.renderer.removeClass(polygon, 'polygon-selected');
			}
		}

		if (nodeRefs) {
			nodeRefs.forEach((ref) => {
				const target = ref.dot;
				if (opening.isSelected) {
					this.renderer.addClass(target, 'node-selected');
				} else {
					this.renderer.removeClass(target, 'node-selected');
				}
			});
		}

		const group = this.polygonLabelMap.get(opening.id);
		if (group && opening.isSelected && opening.displayNumber) {
			this.positionPolygonLabel(opening, group);
			const labelNumber = group.querySelector(`.${SELECTORS.polygon_label_number}`) as SVGTextElement;
			const labelText = group.querySelector(`.${SELECTORS.polygon_label_text}`) as SVGTextElement;
			const labelCircle = group.querySelector(`.${SELECTORS.polygon_label_circle}`) as SVGCircleElement;

			if (labelNumber) {
				labelNumber.textContent = opening.displayNumber.toString();
			}
			if (labelText) {
				labelText.textContent = opening.name;
			}

			const groupVariant = opening.blindType === 'outdoor' ? SELECTORS.polygon_label_group_outdoor : SELECTORS.polygon_label_group_interior;
			const circleVariant =
				opening.blindType === 'outdoor' ? SELECTORS.polygon_label_circle_outdoor : SELECTORS.polygon_label_circle_interior;

			this.renderer.removeClass(group, SELECTORS.polygon_label_group_outdoor);
			this.renderer.removeClass(group, SELECTORS.polygon_label_group_interior);
			this.renderer.removeClass(labelCircle, SELECTORS.polygon_label_circle_outdoor);
			this.renderer.removeClass(labelCircle, SELECTORS.polygon_label_circle_interior);

			this.renderer.addClass(group, groupVariant);
			this.renderer.addClass(labelCircle, circleVariant);

			this.renderer.setStyle(group, 'display', 'block');
		} else if (group) {
			this.renderer.setStyle(group, 'display', 'none');
		}
	}

	private onManualDrawStart = (event: MouseEvent): void => {
		const rect = this.svgRef.nativeElement.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;

		this.drawStart = { x, y };
		this.drawing = true;

		this.previewPolygon = this.renderer.createElement('polygon', 'svg');
		this.renderer.addClass(this.previewPolygon, 'custom-polygon');
		this.renderer.appendChild(this.polygonLayerRef.nativeElement, this.previewPolygon);

		for (let i = 0; i < 4; i++) {
			const circle = this.renderer.createElement('circle', 'svg');
			this.renderer.setAttribute(circle, 'r', '5');
			this.renderer.addClass(circle, 'custom-node');
			this.renderer.appendChild(this.polygonLayerRef.nativeElement, circle);
			this.previewNodes.push(circle);
		}

		if (this.unlistenManualDrawMouseMove) {
			this.unlistenManualDrawMouseMove();
		}
		if (this.unlistenManualDrawMouseUp) {
			this.unlistenManualDrawMouseUp();
		}
		this.unlistenManualDrawMouseMove = this.renderer.listen('document', 'mousemove', this.onManualDrawMove);
		this.unlistenManualDrawMouseUp = this.renderer.listen('document', 'mouseup', this.onManualDrawEnd);
	};

	private onManualDrawMove = (event: MouseEvent): void => {
		if (!this.drawing || !this.drawStart || !this.previewPolygon) {
			return;
		}

		const rect = this.svgRef.nativeElement.getBoundingClientRect();
		const x2 = event.clientX - rect.left;
		const y2 = event.clientY - rect.top;

		const x1 = this.drawStart.x;
		const y1 = this.drawStart.y;

		const corners = [
			{ x: x1, y: y1 },
			{ x: x2, y: y1 },
			{ x: x2, y: y2 },
			{ x: x1, y: y2 },
		];

		const points = corners.map((p) => `${p.x},${p.y}`).join(' ');
		this.previewPolygon.setAttribute('points', points);

		corners.forEach((pt, i) => {
			const node = this.previewNodes[i];
			this.renderer.setAttribute(node, 'cx', pt.x.toString());
			this.renderer.setAttribute(node, 'cy', pt.y.toString());
		});
		this.resetTipsIdleTimer();
	};

	private onManualDrawEnd = (event: MouseEvent): void => {
		if (!this.drawing || !this.drawStart || !this.previewPolygon) {
			return;
		}

		const rect = this.svgRef.nativeElement.getBoundingClientRect();
		const rawX2 = event.clientX - rect.left;
		const rawY2 = event.clientY - rect.top;

		const xMin = Math.min(this.drawStart.x, rawX2);
		const xMax = Math.max(this.drawStart.x, rawX2);
		const yMin = Math.min(this.drawStart.y, rawY2);
		const yMax = Math.max(this.drawStart.y, rawY2);

		let tlX = xMin;
		let tlY = yMin;
		let brX = xMax;
		let brY = yMax;

		const width = xMax - xMin;
		const height = yMax - yMin;

		if (width < 10 && height < 10) {
			// creates a bigger box if the user just clicked
			const midX = (xMin + xMax) / 2;
			const midY = (yMin + yMax) / 2;

			tlX = midX - 50;
			tlY = midY - 50;
			brX = midX + 50;
			brY = midY + 50;
		}

		const newCoords = [
			{ x: tlX, y: tlY },
			{ x: brX, y: tlY },
			{ x: brX, y: brY },
			{ x: tlX, y: brY },
		];

		const newOpening: Opening = {
			id: this.nextOpeningId++,
			coordinates: newCoords,
			isSelected: false,
			isFitted: false,
			blindId: null,
			displayNumber: null,
			name: null,
			blindType: null,
		};

		this.openings.push(newOpening);
		this.createInteractivePolygon(newOpening);

		// Cleanup
		this.previewPolygon?.remove();
		this.previewNodes.forEach((node) => node.remove());
		this.previewNodes = [];
		this.previewPolygon = null;
		this.drawing = false;
		this.drawStart = null;

		if (this.unlistenManualDrawMouseMove) {
			this.unlistenManualDrawMouseMove();
		}
		if (this.unlistenManualDrawMouseUp) {
			this.unlistenManualDrawMouseUp();
		}
		this.resetTipsIdleTimer();
	};

	public getOpenings(): Opening[] {
		return this.openings;
	}

	public setOpenings(openings: Opening[]): void {
		this.openings = openings;
		this.sessionStorageService.removeData(STORAGE_NAMES.zip_openings_data);
		const tempOpenings = structuredClone(this.openings);
		this.sessionStorageService.setSession(this.unScaleOpenings(tempOpenings), STORAGE_NAMES.zip_openings_data);
		this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
	}

	public resetOnSkip(): void {
		this.openings = this.savePointOpenings;
		this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const blindListIds = this.blindListData.map((b) => b.blind_id);

		this.clearSvg();
		this.openings.forEach((opening) => {
			if (opening.blindId && !blindListIds.includes(opening.blindId)) {
				opening.isSelected = false; // TODO: functionlise this
				opening.isFitted = false;
				opening.blindId = null;
				opening.blindType = null;
				opening.displayNumber = null;
				opening.name = null;
			}

			this.createInteractivePolygon(opening);
			// Need this as need to wait for the DOM to be ready so BBox doesn't return 0; // Skips the first emission as SelectOpeningMode is true when this is registered and we want it to go true => false => true
			this.shareService.getSelectOpeningMode.pipe(skip(1), filter(Boolean), first()).subscribe(() => {
				this.applyPolygonSelectionStyle(opening);
			});
		});

		this.reorderDisplayNumbers();
	}

	/**
	 * Opens a dialog with the given component and configuration, but waits for the current macrotask to finish
	 * before opening it.
	 *
	 * On Android, a modal might autoly close by itself
	 * The reason is, we use 'pointerup' event to open ths modal, on Android, event 'click' will be triggered after the pointer event ends
	 * Then the modal will be closed as soon as it is opened
	 * So we move the opening to the end of this event lopp to provent 'click' event closes itself on Android
	 *
	 * @note If it is also needed in other places, extract this function to global unility/helper
	 *
	 * @param dialog The {@link MatDialog} to use for opening the dialog.
	 * @param component The component to use for the dialog.
	 * @param config The configuration to use for the dialog.
	 * @returns A promise that resolves to the result of the dialog when it is closed, or undefined if the dialog
	 * is not closed.
	 */
	private async openDialogWithDelay<T, D = object, R = unknown>(
		dialog: MatDialog,
		component: ComponentType<T>,
		config: MatLegacyDialogConfig<D>,
	): Promise<R | undefined> {
		return new Promise<R | undefined>((resolve) => {
			setTimeout(() => {
				const ref = dialog.open<T, D, R>(component, config);
				firstValueFrom(ref.afterClosed())
					.then((result) => resolve(result))
					.catch(() => resolve(undefined));
			});
		});
	}
}
